import { DatabaseData, DatabaseInfo, TableInfo, ColumnInfo, RowData } from '../types/database'

export class JSONService {
  static async parseJSON(file: File): Promise<DatabaseData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      
      reader.onload = () => {
        try {
          const content = reader.result as string
          const jsonData = JSON.parse(content)
          
          const data = this.convertJSONToDatabaseData(jsonData, file)
          resolve(data)
        } catch (error) {
          reject(new Error(`JSONパースエラー: ${error instanceof Error ? error.message : '不明なエラー'}`))
        }
      }
      
      reader.onerror = () => {
        reject(new Error('ファイル読み込みエラー'))
      }
      
      reader.readAsText(file)
    })
  }

  private static convertJSONToDatabaseData(jsonData: any, file: File): DatabaseData {
    let tables: DatabaseData['tables'] = {}
    let tableInfos: TableInfo[] = []

    if (Array.isArray(jsonData)) {
      // 配列の場合
      if (jsonData.length === 0) {
        throw new Error('JSON配列が空です')
      }

      const columns = this.inferColumnsFromArray(jsonData)
      const tableInfo: TableInfo = {
        name: 'data',
        columns,
        rowCount: jsonData.length
      }

      tables.data = {
        columns,
        rows: jsonData.map((row, index) => ({
          ...row,
          __rowIndex: index
        }))
      }

      tableInfos.push(tableInfo)
    } else if (typeof jsonData === 'object' && jsonData !== null) {
      // オブジェクトの場合
      const keys = Object.keys(jsonData)
      
      if (keys.length === 0) {
        throw new Error('JSONオブジェクトが空です')
      }

      // 各キーをテーブルとして扱う
      for (const key of keys) {
        const value = jsonData[key]
        
        if (Array.isArray(value)) {
          const columns = this.inferColumnsFromArray(value)
          const tableInfo: TableInfo = {
            name: key,
            columns,
            rowCount: value.length
          }

          tables[key] = {
            columns,
            rows: value.map((row, index) => ({
              ...row,
              __rowIndex: index
            }))
          }

          tableInfos.push(tableInfo)
        } else if (typeof value === 'object' && value !== null) {
          // オブジェクトの場合、単一の行として扱う
          const columns = this.inferColumnsFromObject(value)
          const tableInfo: TableInfo = {
            name: key,
            columns,
            rowCount: 1
          }

          tables[key] = {
            columns,
            rows: [{
              ...value,
              __rowIndex: 0
            }]
          }

          tableInfos.push(tableInfo)
        }
      }
    } else {
      throw new Error('サポートされていないJSON形式です')
    }

    const info: DatabaseInfo = {
      type: 'json',
      name: file.name,
      size: file.size,
      tables: tableInfos
    }

    return { info, tables }
  }

  private static inferColumnsFromArray(array: any[]): ColumnInfo[] {
    if (array.length === 0) return []

    const firstItem = array[0]
    if (typeof firstItem === 'object' && firstItem !== null) {
      return Object.keys(firstItem).map(key => ({
        name: key,
        type: this.inferColumnType(array, key),
        nullable: true,
        primaryKey: false
      }))
    } else {
      // プリミティブ値の配列の場合
      return [{
        name: 'value',
        type: this.inferPrimitiveType(array),
        nullable: true,
        primaryKey: false
      }]
    }
  }

  private static inferColumnsFromObject(obj: any): ColumnInfo[] {
    return Object.keys(obj).map(key => ({
      name: key,
      type: this.inferValueType(obj[key]),
      nullable: true,
      primaryKey: false
    }))
  }

  private static inferColumnType(array: any[], columnName: string): string {
    const values = array.map(item => item[columnName]).filter(val => val !== null && val !== undefined)
    
    if (values.length === 0) return 'TEXT'

    return this.inferPrimitiveType(values)
  }

  private static inferPrimitiveType(values: any[]): string {
    if (values.length === 0) return 'TEXT'

    const sampleValues = values.slice(0, 10) // 最初の10個の値で型を推測

    // 数値型のチェック
    const numericValues = sampleValues.filter(val => typeof val === 'number' || (!isNaN(Number(val)) && val !== ''))
    if (numericValues.length === sampleValues.length) {
      const numbers = numericValues.map(Number)
      const hasDecimals = numbers.some(num => num % 1 !== 0)
      return hasDecimals ? 'REAL' : 'INTEGER'
    }

    // 真偽値のチェック
    const booleanValues = sampleValues.filter(val => typeof val === 'boolean')
    if (booleanValues.length === sampleValues.length) {
      return 'BOOLEAN'
    }

    // 日付型のチェック
    const dateValues = sampleValues.filter(val => {
      if (typeof val !== 'string') return false
      const date = new Date(val)
      return !isNaN(date.getTime())
    })
    if (dateValues.length === sampleValues.length) {
      return 'DATETIME'
    }

    return 'TEXT'
  }

  private static inferValueType(value: any): string {
    if (typeof value === 'number') {
      return value % 1 === 0 ? 'INTEGER' : 'REAL'
    }
    if (typeof value === 'boolean') return 'BOOLEAN'
    if (typeof value === 'string') {
      const date = new Date(value)
      return !isNaN(date.getTime()) ? 'DATETIME' : 'TEXT'
    }
    if (value === null || value === undefined) return 'TEXT'
    return 'TEXT'
  }

  static exportJSON(data: DatabaseData, originalFileName: string): Blob {
    const result: any = {}

    for (const [tableName, table] of Object.entries(data.tables)) {
      if (table.rows.length === 1) {
        // 単一行の場合はオブジェクトとして出力
        const row = table.rows[0]
        const obj: any = {}
        for (const column of table.columns) {
          obj[column.name] = row[column.name]
        }
        result[tableName] = obj
      } else {
        // 複数行の場合は配列として出力
        result[tableName] = table.rows.map(row => {
          const obj: any = {}
          for (const column of table.columns) {
            obj[column.name] = row[column.name]
          }
          return obj
        })
      }
    }

    const content = JSON.stringify(result, null, 2)
    return new Blob([content], { type: 'application/json' })
  }
} 